/*
 * Copyright (c) 2008-2025 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */
import { LOAD_STATES } from "@/utils/PagedRequestHandler"
import { castValueToType, defaultTypeValueFactory, VALUE_TYPES } from "@/utils/TypeCastHelpers"
import Vue from "vue"
import api from "@/config/api"
import { GLOBAL_ATTRIBUTE_KEY, LICENSE_FEATURES } from "@/config/licensing_constants"
import { EventBus } from "~/common/src/event-bus"
import {
  LICENSING_PACKAGES_UPDATED,
  LICENSING_ASSIGNMENTS_UPDATED,
  TENANTS_ASSIGNED_TENANTS_UPDATED,
} from "../config/events"
import store from "./index"

// Register message listeners to refresh effective licenses when any of the following data sets is updated locally:
// - local set of license packages
// - local set of license assignments
// - local set of tenants
// ToDo (pending): Refreshing shall instead be triggered by the backend via pub/sub when a subscribed effective license
//  changes (e.g. by package update, assignment update or expiry).
EventBus.$on([LICENSING_PACKAGES_UPDATED, LICENSING_ASSIGNMENTS_UPDATED, TENANTS_ASSIGNED_TENANTS_UPDATED], () => {
  // Ignore request to dispatch if the mechanism is disabled.
  if (!store.getters["settings/isLicenseMechanismEnabled"]) {
    return
  }

  store.dispatch("effectiveLicenses/loadEffectiveLicenses")
})

export default {
  namespaced: true,

  state: {
    /**
     * Intended end structure
     * {
     *  tenantId: {
     *              effectiveFeatureConfigs: {} // Server defined structure
     *              licenseAssignment: {} // Server defined structure
     *              licensePackage: {} // Server defined structure
     *            }
     * }
     */
    effectiveLicenses: {},
    loadStatus: LOAD_STATES.UNLOADED,
  },

  mutations: {
    /**
     * Clears current state and stores the requested set of effective license information
     *
     * @param {Object} effectiveLicenses - detailing the following, currently effective, information per tenant:
     * license package
     * license assignment (if any)
     * resulting license feature set
     */
    setEffectiveLicenses(state, { effectiveLicenses }) {
      Vue.set(state, "effectiveLicenses", {})

      effectiveLicenses.forEach((license) => {
        Vue.set(state.effectiveLicenses, license.tenantId, license)
      })
    },

    /**
     * Sets the load status of the effective license to state
     *
     * @param {LOAD_STATES} status - of load of effective license
     */
    setEffectiveLicenseStatus(state, status) {
      Vue.set(state, "loadStatus", status)
    },
  },

  actions: {
    /**
     * Perform request to retrieve the effective license configs for all tenants assigned to any object
     * currently held in store (meetings, users, etc.).
     *
     * (Delegating to tenant store module to get respective tenantIds)
     *
     * @returns
     */
    async loadEffectiveLicenses({ commit, dispatch, rootGetters }, tenantIds = []) {
      if (!tenantIds.length) {
        tenantIds = rootGetters["tenants/getAssignedTenantIds"]
      }

      // Trigger loading of all required data in parallel to reduce impact on app startup time.
      const systemConfigPromise = dispatch("settings/loadSystemConfiguration", null, { root: true })
      const effectiveLicensesPromise = Vue.http.get(api.TENANT_EFFECTIVE_LICENSES(tenantIds))

      // Ignore request to dispatch if the mechanism is disabled.
      await systemConfigPromise
      if (!rootGetters["settings/isLicenseMechanismEnabled"]) {
        return
      }

      try {
        const effectiveLicenses = (await effectiveLicensesPromise)?.body?.content
        commit("setEffectiveLicenses", { effectiveLicenses })
        commit("setEffectiveLicenseStatus", LOAD_STATES.LOADED)
      } catch (error) {
        commit("setEffectiveLicenseStatus", LOAD_STATES.ERROR)
        throw new Error(error.body)
      }
    },
  },

  getters: {
    /**
     * Returns whether the effective license has been loaded to state.
     * @returns Boolean
     */
    isEffectiveLicenseNotLoaded: (state) =>
      state.loadStatus !== LOAD_STATES.LOADED && state.loadStatus !== LOAD_STATES.LOADING,

    /**
     * Returns the license package currently in effect for the specified tenant,
     * if existing, or null otherwise.
     *
     * @param {*} state
     * @returns Object | null
     */
    getLicensePackage:
      (state) =>
      (tenantId = null) => {
        if (!state.effectiveLicenses[tenantId]) {
          return null
        }
        return state.effectiveLicenses[tenantId]?.licensePackage
      },

    /**
     * Returns the license assignment currently in effect for the specified tenant,
     * if existing, or null otherwise.
     *
     * @param {*} state
     * @returns Object
     */
    getLicenseAssignment:
      (state) =>
      (tenantId = null) => {
        if (!state.effectiveLicenses[tenantId]) {
          return null
        }
        return state.effectiveLicenses[tenantId].licenseAssignment
      },

    /**
     * Returns the value currently in effect for the specified tenant, license feature and sub-qualifier.
     *
     * If license mechanism is disabled or license data not yet loaded inverts the return value in accordance
     * with the base type.
     *
     * @param  {...any} param0
     * @returns {@see VALUE_TYPES}
     */
    getEffectiveLicenseFeature:
      (
        ...[state, , , rootGetters] // For this syntax see: https://stackoverflow.com/a/58738236
      ) =>
      (featureName, qualifier, baseType, tenantId) => {
        const defaultTypeValue = defaultTypeValueFactory({
          [VALUE_TYPES.NUMBER]: -1,
        })

        if (!rootGetters["settings/isLicenseMechanismEnabled"] || state.loadStatus !== LOAD_STATES.LOADED) {
          // Gets the default value for a type inverted to always return the "truthy" version of the type
          const invertedDefaultTypeValue = defaultTypeValueFactory({
            [VALUE_TYPES.NUMBER]: -1, // -1 defines infinity number of feature type
            [VALUE_TYPES.BOOLEAN]: true,
          })

          return invertedDefaultTypeValue(baseType)
        }

        // Contains the aggregate set of features from both overrides and base package
        let licenseFeatures = state.effectiveLicenses[tenantId]?.effectiveFeatureConfigs

        if (!qualifier) {
          qualifier = GLOBAL_ATTRIBUTE_KEY
        }

        if (!licenseFeatures || !licenseFeatures[featureName] || !licenseFeatures[featureName][qualifier]) {
          return defaultTypeValue(baseType)
        }

        let featureValue = licenseFeatures[featureName][qualifier]

        return castValueToType(featureValue, baseType)
      },

    /**
     * Returns the number of meeting rooms of the specified type licensed to the specified tenant.
     *
     * @param state
     * @param getters
     * @returns The number of licensed meeting rooms
     */
    meetingRoomCount: (state, getters) => (type, tenantId) =>
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.MEETING_ROOM_COUNT, type, VALUE_TYPES.NUMBER, tenantId),

    /**
     * Returns the meeting room capacity (allowed participants in a meeting room) licensed to the specified tenant.
     *
     * @param state
     * @param getters
     * @returns The capacity of the specified tenant's meeting rooms
     */
    meetingRoomCapacity: (state, getters) => (tenantId) =>
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.MEETING_ROOM_CAPACITY, null, VALUE_TYPES.NUMBER, tenantId),

    /**
     * Returns whether the waiting room feature is licensed to the specified tenant.
     *
     * @param state
     * @param getters
     * @returns {function(*): *}
     */
    canUseWaitingRoom: (state, getters) => (tenantId) =>
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.WAITING_ROOM, null, VALUE_TYPES.BOOLEAN, tenantId),

    /**
     * Returns whether accessing the meeting results is licensed to the specified tenant.
     *
     * @param {*} state
     * @param {*} getters
     * @returns true if the meeting results are available, false otherwise
     */
    isMeetingResultsLicensed: (state, getters) => (tenantId) =>
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.MEETING_RESULTS, null, VALUE_TYPES.BOOLEAN, tenantId),

    /**
     * Returns whether multi-user capability is licensed to the specified tenant.
     *
     * @param {*} state
     * @param {*} getters
     * @returns Boolean
     */
    canCreateMultipleUsers: (state, getters) => (tenantId) =>
      !tenantId ||
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.MULTI_USER, null, VALUE_TYPES.BOOLEAN, tenantId),

    /**
     * Returns whether time tracking is licensed to the specified tenant.
     */
    isTimeTrackingLicensed: (state, getters) => (tenantId) =>
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.TIME_TRACKING, null, VALUE_TYPES.BOOLEAN, tenantId),

    /**
     * Returns whether the meeting cockpit is licensed to the specified tenant.
     * (Non-tenant users (specified tenantId is null) can always use the meeting cockpit).
     *
     * @param {*} state
     * @param {*} getters
     * @returns Boolean true if the cockpit is licensed, false otherwise
     */
    canUseMeetingCockpit: (state, getters) => (tenantId) =>
      !tenantId ||
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.JMC_USAGE, null, VALUE_TYPES.BOOLEAN, tenantId),

    /**
     * Returns whether role-based meeting sharing is licensed to the specified tenant.
     *
     * @param {*} state
     * @param {*} getters
     * @returns Boolean
     */
    isRoleBasedMeetingSharingLicensed: (state, getters) => (tenantId) =>
      getters["getEffectiveLicenseFeature"](
        LICENSE_FEATURES.ROLE_BASED_MEETING_SHARING,
        null,
        VALUE_TYPES.BOOLEAN,
        tenantId
      ),

    /**
     * Returns whether proxy voting is licensed to the specified tenant.
     */
    isProxyVotingLicensed: (state, getters) => (tenantId) =>
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.PROXY_VOTING, null, VALUE_TYPES.BOOLEAN, tenantId),

    /**
     * Returns whether configuration of SSO as login method is licensed to the specified tenant.
     *
     * @param {*} state
     * @param {*} getters
     * @returns true if the configuration of SSO login is available, false otherwise
     */
    isSSOConfigLicensed: (state, getters) => (tenantId) =>
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.SSO_LOGIN_CONFIG, null, VALUE_TYPES.BOOLEAN, tenantId),

    /**
     * Returns whether simpleFeedbackView is licensed to the specified tenant.
     *
     * @param {*} state
     * @param {*} getters
     * @returns true if simpleFeedbackView is available, false otherwise
     */
    isSimpleFeedbackViewLicensed: (state, getters) => (tenantId) =>
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.SIMPLE_FEEDBACK_VIEW, null, VALUE_TYPES.BOOLEAN, tenantId),

    /**
     * Returns whether accreditation is licensed to the specified tenant.
     *
     * @param {*} state
     * @param {*} getters
     * @returns true if simpleFeedbackView is available, false otherwise
     */
    isAccreditationLicensed: (state, getters) => (tenantId) =>
      getters["getEffectiveLicenseFeature"](LICENSE_FEATURES.ACCREDITATION, null, VALUE_TYPES.BOOLEAN, tenantId),
  },
}
